import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSymptoms, getMigrationSites } from "../lib/api";
import { IssueFinder } from "../components/IssueFinder";
import {
  AudioLines, VolumeX, PhoneOff, Activity, Repeat, PhoneMissed, Hash,
  Network, Lock, ArrowRight, Layers, Clock, ShieldAlert, ChevronRight, Cloud,
  CloudOff, Users, VideoOff, PhoneOutgoing,
} from "lucide-react";

const ICONS = {
  "audio-lines": AudioLines, "volume-x": VolumeX, "phone-off": PhoneOff,
  activity: Activity, repeat: Repeat, "phone-missed": PhoneMissed, hash: Hash,
  network: Network, lock: Lock,
  "cloud-off": CloudOff, users: Users, "video-off": VideoOff, "phone-outgoing": PhoneOutgoing,
};

const SEV = {
  critical: { label: "Critical", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  warning: { label: "Warning", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  info: { label: "Info", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
};

export default function Dashboard() {
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    getSymptoms().then((d) => { setSymptoms(d); setLoading(false); }).catch(() => setLoading(false));
    getMigrationSites().then(setSites).catch(() => {});
  }, []);

  const cutover = sites.filter((s) => s.status === "cutover").length;
  const onboarded = sites.filter((s) => s.status === "onboarded").length;
  const migPct = sites.length ? Math.round((cutover / sites.length) * 100) : 0;

  return (
    <div>
      {/* Header */}
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> diagnostics <ChevronRight className="h-3 w-3" /> symptom index
        </div>
      </header>

      <div className="p-5 sm:p-8 max-w-6xl">
        {sites.length > 0 && (
          <button
            onClick={() => navigate("/migration")}
            data-testid="migration-overview-card"
            className="group w-full text-left rounded-lg border border-zinc-800 bg-zinc-900 p-5 mb-8 hover:border-blue-600/40 transition-colors"
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-md bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
                  <Cloud className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <div className="font-heading font-bold text-white text-lg">Cloud Migration in progress</div>
                  <div className="text-[12px] text-zinc-400 mt-0.5">
                    <span className="text-emerald-400 font-semibold">{cutover} cutover</span> ·{" "}
                    <span className="text-blue-400 font-semibold">{onboarded} onboarded</span> ·{" "}
                    {sites.length - cutover - onboarded} pending — across {sites.length} sites
                  </div>
                </div>
              </div>
              <span className="flex items-center gap-1 text-sm text-blue-400 group-hover:gap-2 transition-[gap]">
                Open playbook <ArrowRight className="h-4 w-4" />
              </span>
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${migPct}%` }} data-testid="migration-overview-progress" />
            </div>
          </button>
        )}

        <div className="mb-8">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Interactive Diagnostics
          </h1>
          <p className="text-zinc-400 mt-3 max-w-2xl text-base">
            Select the symptom you are seeing in the field. Each guide walks you layer-by-layer —
            from cabling through signaling and media — with exact CLI commands, CUCM/RTMT paths, and
            what <span className="text-emerald-400">good output</span> looks like.
          </p>
        </div>

        <div className="mb-8"><IssueFinder /></div>

        {loading ? (
          <div className="text-zinc-500 font-mono text-sm">Loading knowledge base…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {symptoms.map((s) => {
              const Icon = ICONS[s.icon] || Activity;
              const sev = SEV[s.severity] || SEV.info;
              return (
                <button
                  key={s.id}
                  data-testid={`symptom-card-${s.id}`}
                  onClick={() => navigate(`/wizard/${s.id}`)}
                  className="group text-left rounded-lg border border-zinc-800 bg-zinc-900 p-5 hover:border-zinc-600 hover:-translate-y-px transition-[transform,border-color] duration-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center group-hover:border-blue-500/40">
                      <Icon className="h-5 w-5 text-blue-400" />
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${sev.cls}`}>
                      {sev.label}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mt-4">{s.title}</h3>
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-semibold">{s.category}</p>
                  <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{s.summary}</p>

                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800 text-[11px] text-zinc-500">
                    <span className="flex items-center gap-1.5"><Layers className="h-3.5 w-3.5" />{s.step_count} steps</span>
                    <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{s.estimated_time}</span>
                    <span className="ml-auto flex items-center gap-1 text-blue-400 group-hover:gap-2 transition-[gap]">
                      Start <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-8 rounded-lg border border-zinc-800 bg-zinc-900 p-5 flex items-center gap-4">
          <ShieldAlert className="h-6 w-6 text-amber-400 shrink-0" />
          <div className="text-sm text-zinc-400">
            <span className="text-white font-medium">Stuck or need something not listed?</span>{" "}
            Ask the <button onClick={() => navigate("/assistant")} data-testid="dashboard-assistant-link" className="text-blue-400 hover:underline">AI Assistant</button> for
            any Cisco VoIP issue — cabling, QoS, SIP, CUCM, gateways and more.
          </div>
        </div>
      </div>
    </div>
  );
}
